import { useMemo, useState, type ReactNode, type CSSProperties, type MouseEvent } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import { Skeleton } from '../Skeleton/Skeleton';
import { Pagination } from '../Pagination/Pagination';
import { tableGridWrapClasses } from './tableShared';
import { sparkAssetUrl } from '../../utils/sparkAssetUrl';
import './Table.css';
import './DataTable.css';

const EXPAND_COL_FLEX: CSSProperties = {
  flex: '0 0 48px',
  minWidth: 48,
  maxWidth: 48,
  flexShrink: 0,
};

const EMPTY_ILLUSTRATION_SRC = sparkAssetUrl('illustrations/illustration-box-open.svg');

const SKELETON_ROW_COUNT = 5;

export type DataTableColumn = {
  key?: string;
  dataIndex?: string;
  title?: ReactNode;
  width?: string | number;
  flex?: number;
  minWidth?: number;
  render?: (value: unknown, record: Record<string, unknown>, index: number) => ReactNode;
  /**
   * Sub-columns rendered under this column's shared group header (two-tier
   * header). The column header stacks `title` above the sub-column titles,
   * and each body cell splits into one bordered sub-cell per child.
   */
  children?: DataTableColumn[];
  /**
   * For grouped columns only: when it returns content for a row, that
   * content spans all sub-columns of the group for that row (e.g. a
   * processing indicator). Return `undefined` to render the sub-cells.
   */
  renderGroup?: (record: Record<string, unknown>, index: number) => ReactNode | undefined;
};

export type DataTablePagination = {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, size?: number) => void;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
};

/** Data-driven table that renders rows from `dataSource` using column definitions. Supports loading skeleton and pagination. */
export type DataTableProps = {
  columns?: DataTableColumn[];
  dataSource?: Record<string, unknown>[];
  rowKey?: string | ((record: Record<string, unknown>) => string);
  loading?: boolean;
  showHeader?: boolean;
  pagination?: DataTablePagination | null;
  /** Content rendered on the left side of the pagination row (e.g. an export button). */
  paginationExtra?: ReactNode;
  emptyText?: ReactNode;
  /** When true, rows do not change their background color on hover. */
  disableHoverBackground?: boolean;
  /** Fixed data-row height in px. Cell content is vertically centered. */
  rowHeight?: number;
  className?: string;
  /** When set, each row gets a 48px expand chevron and a custom expanded panel. */
  expandedRowRender?: (record: Record<string, unknown>, index: number) => ReactNode;
  /** Controlled expanded row keys. Omit for internal expand state. */
  expandedRowKeys?: string[];
  onExpandedChange?: (keys: string[]) => void;
  /** Highlights the matching row. Compared against `rowKey`. */
  selectedRowKey?: string | null;
  onRowClick?: (record: Record<string, unknown>, index: number) => void;
};

function isNarrowColumn(width: DataTableColumn['width']): boolean {
  return width === '1%' || (typeof width === 'number' && width <= 80);
}

function colToFlexStyle(col: DataTableColumn): CSSProperties {
  const { width, flex, minWidth: colMinWidth } = col;
  const minPx = typeof colMinWidth === 'number' && Number.isFinite(colMinWidth) ? colMinWidth : undefined;
  // minWidth columns must not use flex-shrink: the shared cell CSS sets
  // min-width:0, which would otherwise clip header titles and filter icons.
  if (width === '1%') return { flex: '0 0 48px', minWidth: 48, maxWidth: 48, flexShrink: 0 };
  if (typeof width === 'number' && width <= 80)
    return { flex: `0 0 ${width}px`, minWidth: width, maxWidth: width, flexShrink: 0 };
  if (typeof width === 'number') {
    const basis = Math.max(width, minPx ?? 0);
    return { flex: `0 0 ${basis}px`, minWidth: minPx ?? width, flexShrink: 0 };
  }
  if (typeof width === 'string') {
    if (width.endsWith('px')) {
      const px = parseInt(width, 10);
      const basis = Number.isFinite(px) ? Math.max(px, minPx ?? 0) : px;
      return { flex: `0 0 ${basis}px`, minWidth: minPx ?? px, flexShrink: 0 };
    }
    return { flex: `0 0 ${width}`, minWidth: minPx, flexShrink: 0 };
  }
  // Grow to fill leftover row space (e.g. Example when few columns are
  // visible). Keep flex-shrink at 0 so minWidth still wins when the table
  // overflows and scrolls horizontally.
  if (flex) {
    return { flex: `${flex} 0 ${minPx ?? 0}px`, minWidth: minPx ?? 0 };
  }
  if (minPx) return { flex: `0 0 ${minPx}px`, minWidth: minPx, flexShrink: 0 };
  return { flex: '1 1 0', minWidth: 0 };
}

function computePages(current: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 1) return [1];
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const WINDOW = 2;
  const start = Math.max(2, current - WINDOW);
  const end = Math.min(totalPages - 1, current + WINDOW);
  const pages: (number | 'ellipsis')[] = [1];
  if (start > 2) pages.push('ellipsis');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < totalPages - 1) pages.push('ellipsis');
  pages.push(totalPages);
  return pages;
}

export function DataTable({
  columns = [],
  dataSource = [],
  rowKey = '_id',
  loading = false,
  showHeader = true,
  pagination = null,
  paginationExtra = null,
  emptyText = 'No data',
  disableHoverBackground = false,
  rowHeight,
  className = '',
  expandedRowRender,
  expandedRowKeys,
  onExpandedChange,
  selectedRowKey = null,
  onRowClick,
}: DataTableProps) {
  const resolveKey = (record: Record<string, unknown>, index: number): string =>
    typeof rowKey === 'function' ? rowKey(record) : String(record[rowKey] ?? index);

  const showExpand = typeof expandedRowRender === 'function';
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState<string[]>([]);
  const expandedKeys = expandedRowKeys ?? uncontrolledExpanded;
  const expandedKeySet = useMemo(() => new Set(expandedKeys), [expandedKeys]);

  const toggleExpanded = (key: string) => {
    const next = expandedKeySet.has(key)
      ? expandedKeys.filter((item) => item !== key)
      : [...expandedKeys, key];
    if (expandedRowKeys === undefined) {
      setUncontrolledExpanded(next);
    }
    onExpandedChange?.(next);
  };

  const hasGroups = columns.some((col) => col.children?.length);
  const rowHeightStyle = rowHeight != null ? { height: rowHeight } : undefined;

  const totalItems = pagination?.total ?? 0;
  const pageSize = pagination?.pageSize ?? 10;
  const currentPage = pagination?.current ?? 1;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pages = computePages(currentPage, totalPages);

  const showEmpty = !loading && dataSource.length === 0;
  const showRows = !loading && dataSource.length > 0;

  const flexStyles = useMemo(() => columns.map(colToFlexStyle), [columns]);
  const tableMinWidth = useMemo(
    () => {
      const columnsWidth = flexStyles.reduce((sum, style) => {
        const minWidth = style.minWidth;
        if (typeof minWidth === 'number' && Number.isFinite(minWidth)) return sum + minWidth;
        if (typeof minWidth === 'string') {
          const px = parseInt(minWidth, 10);
          return Number.isFinite(px) ? sum + px : sum;
        }
        return sum;
      }, 0);
      return columnsWidth + (showExpand ? 48 : 0);
    },
    [flexStyles, showExpand],
  );

  const renderDataCells = (record: Record<string, unknown>, rowIndex: number) =>
    columns.map((col, colIndex) => {
      const colKey = col.key ?? col.dataIndex ?? String(colIndex);
      const narrow = isNarrowColumn(col.width);
      const cls = [
        'spark-datatable-cell',
        'spark-datatable-data-cell',
        narrow ? 'spark-datatable-col-narrow' : '',
      ].filter(Boolean).join(' ');
      if (col.children?.length) {
        const spanContent = col.renderGroup?.(record, rowIndex);
        return (
          <div
            key={colKey}
            className={`${cls} spark-datatable-group-cell`}
            role="cell"
            style={{ ...flexStyles[colIndex], ...rowHeightStyle }}
          >
            {spanContent != null ? (
              <div className="spark-datatable-group-span">{spanContent}</div>
            ) : (
              col.children.map((child, childIndex) => {
                const childValue = child.dataIndex != null ? record[child.dataIndex] : undefined;
                return (
                  <div
                    key={child.key ?? child.dataIndex ?? String(childIndex)}
                    className="spark-datatable-subcell"
                    role="cell"
                  >
                    {child.render
                      ? child.render(childValue, record, rowIndex)
                      : (childValue as ReactNode)}
                  </div>
                );
              })
            )}
          </div>
        );
      }
      const cellValue = col.dataIndex != null ? record[col.dataIndex] : undefined;
      const cellContent = col.render
        ? col.render(cellValue, record, rowIndex)
        : (cellValue as ReactNode);
      return (
        <div key={colKey} className={cls} role="cell" style={{ ...flexStyles[colIndex], ...rowHeightStyle }}>
          {cellContent}
        </div>
      );
    });

  const expandHeaderCell = showExpand ? (
    <div
      className="spark-datatable-cell spark-datatable-header-cell spark-datatable-col-narrow spark-datatable-expand-cell"
      role="columnheader"
      style={EXPAND_COL_FLEX}
    />
  ) : null;

  const renderExpandCell = (key: string, expanded: boolean) => (
    <div
      className="spark-datatable-cell spark-datatable-data-cell spark-datatable-col-narrow spark-datatable-expand-cell"
      role="cell"
      style={{ ...EXPAND_COL_FLEX, ...rowHeightStyle }}
    >
      <Button
        variant="ghost"
        size="small"
        className="spark-table-expand-toggle-btn"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse row' : 'Expand row'}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          toggleExpanded(key);
        }}
        icon={
          <SparkIcon
            slug={expanded ? 'chevron-down-large' : 'chevron-right-large'}
            size="small"
          />
        }
      />
    </div>
  );

  return (
    <div
      className={[
        'spark-datatable-wrapper',
        showEmpty ? 'spark-datatable-wrapper-empty' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={tableGridWrapClasses({
          size: 'medium',
          innerBorder: 'no',
          outerBorder: 'yes',
          extraClass: 'spark-datatable-grid-wrap',
        })}
      >
        <div
          className={[
            'spark-datatable-body',
            disableHoverBackground ? 'spark-datatable-no-hover' : '',
            rowHeight != null ? 'spark-datatable-fixed-rows' : '',
          ].filter(Boolean).join(' ')}
          role="table"
          style={tableMinWidth > 0 ? { minWidth: tableMinWidth } : undefined}
        >
          {showHeader && (
            <div
              className={[
                'spark-datatable-row',
                'spark-datatable-header-row',
                hasGroups ? 'spark-datatable-header-row-grouped' : '',
              ].filter(Boolean).join(' ')}
              role="row"
            >
              {expandHeaderCell}
              {columns.map((col, colIndex) => {
                const key = col.key ?? col.dataIndex ?? String(colIndex);
                const narrow = isNarrowColumn(col.width);
                const cls = [
                  'spark-datatable-cell',
                  'spark-datatable-header-cell',
                  narrow ? 'spark-datatable-col-narrow' : '',
                ].filter(Boolean).join(' ');
                if (col.children?.length) {
                  return (
                    <div
                      key={`h-${key}`}
                      className={`${cls} spark-datatable-group-header`}
                      role="columnheader"
                      style={flexStyles[colIndex]}
                    >
                      <div className="spark-datatable-group-title">{col.title ?? null}</div>
                      <div className="spark-datatable-subheader-row">
                        {col.children.map((child, childIndex) => (
                          <div
                            key={child.key ?? child.dataIndex ?? String(childIndex)}
                            className="spark-datatable-subheader-cell"
                            role="columnheader"
                          >
                            {child.title ?? null}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={`h-${key}`} className={cls} role="columnheader" style={flexStyles[colIndex]}>
                    {col.title ?? null}
                  </div>
                );
              })}
            </div>
          )}

          {loading &&
            Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
              <div className="spark-datatable-row" role="row" key={`skeleton-row-${rowIndex}`}>
                {showExpand ? (
                  <div
                    className="spark-datatable-cell spark-datatable-data-cell spark-datatable-col-narrow spark-datatable-expand-cell"
                    role="cell"
                    style={{ ...EXPAND_COL_FLEX, ...rowHeightStyle }}
                  >
                    <Skeleton shapeType="paragraph" width="16px" />
                  </div>
                ) : null}
                {columns.map((col, colIndex) => {
                  const key = col.key ?? col.dataIndex ?? String(colIndex);
                  const narrow = isNarrowColumn(col.width);
                  const cls = [
                    'spark-datatable-cell',
                    'spark-datatable-data-cell',
                    narrow ? 'spark-datatable-col-narrow' : '',
                  ].filter(Boolean).join(' ');
                  if (col.children?.length) {
                    return (
                      <div
                        key={key}
                        className={`${cls} spark-datatable-group-cell`}
                        role="cell"
                        style={{ ...flexStyles[colIndex], ...rowHeightStyle }}
                      >
                        {col.children.map((child, childIndex) => (
                          <div
                            key={child.key ?? child.dataIndex ?? String(childIndex)}
                            className="spark-datatable-subcell spark-datatable-subcell-loading"
                            role="cell"
                          >
                            <Skeleton shapeType="paragraph" width="80%" />
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <div key={key} className={cls} role="cell" style={{ ...flexStyles[colIndex], ...rowHeightStyle }}>
                      <Skeleton shapeType="paragraph" width="80%" />
                    </div>
                  );
                })}
              </div>
            ))}

          {showEmpty && emptyText && (
            <div className="spark-datatable-empty" role="row">
              <img
                className="spark-datatable-empty-illustration"
                src={EMPTY_ILLUSTRATION_SRC}
                alt=""
                aria-hidden="true"
              />
              <span className="spark-datatable-empty-text">{emptyText}</span>
            </div>
          )}

          {showRows &&
            dataSource.map((record, rowIndex) => {
              const key = resolveKey(record, rowIndex);
              const expanded = showExpand && expandedKeySet.has(key);
              const selected = selectedRowKey != null && key === String(selectedRowKey);
              return (
                <div className="spark-datatable-row-group" key={key}>
                  <div
                    className={[
                      'spark-datatable-row',
                      onRowClick ? 'spark-datatable-row-clickable' : '',
                      selected ? 'spark-datatable-row-selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="row"
                    onClick={onRowClick ? () => onRowClick(record, rowIndex) : undefined}
                  >
                    {showExpand ? renderExpandCell(key, expanded) : null}
                    {renderDataCells(record, rowIndex)}
                  </div>
                  {expanded ? (
                    <div className="spark-datatable-expanded-panel" role="region">
                      {expandedRowRender?.(record, rowIndex)}
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
      </div>

      {((pagination && totalItems > 0) || paginationExtra) && (
        <div
          className="spark-table-pagination-wrap spark-datatable-pagination-row"
          style={{ paddingTop: 12, paddingBottom: 12 }}
        >
          {paginationExtra && (
            <div className="spark-datatable-pagination-extra">{paginationExtra}</div>
          )}
          {pagination && totalItems > 0 && (
            <Pagination
              type="total-items"
              currentPage={currentPage}
              totalPages={totalPages}
              pages={pages}
              totalItems={totalItems}
              pageSize={pageSize}
              pageSizeLabel={`${pageSize} / Page`}
              pageSizeOptions={pagination.pageSizeOptions}
              onPageSizeChange={pagination.onPageSizeChange}
              onPageChange={pagination.onChange}
              onPrevious={() => currentPage > 1 && pagination.onChange(currentPage - 1)}
              onNext={() => currentPage < totalPages && pagination.onChange(currentPage + 1)}
            />
          )}
        </div>
      )}
    </div>
  );
}
